import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Pagination } from 'semantic-ui-react'
import moment from 'moment'
import { Spinner, Alert } from 'react-bootstrap'
import { SegmentForm } from '@thairath/dmp-segment'
import ExportSegmentModal from './ExportSegmentModal'
import admin from '../../libs/admin'
import { checkPermission } from '../../helper/functions'

var ReactBsTable = require("react-bootstrap-table")
var BootstrapTable = ReactBsTable.BootstrapTable
var TableHeaderColumn = ReactBsTable.TableHeaderColumn

const ERROR_MESSAGES = {
    SERVER_ERROR: 'เกิดข้อผิดพลาดของเซิร์ฟเวอร์ กรุณาลองใหม่อีกครั้ง',
    NETWORK_ERROR: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต',
    DEFAULT: 'เกิดข้อผิดพลาดในการดึงข้อมูล Segment'
}

const Dashboard = (props) => {
    const user = admin.getCookieData()
    const hasViewPermission = checkPermission(user.permission, 'audience_builder_view')
    const hasEditPermission = checkPermission(user.permission, 'audience_builder_edit')

    const [segments, setSegments] = useState([])
    const [filterText, setFilterText] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [activePage, setActivePage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [openExportModal, setOpenExportModal] = useState(false)
    const [selectedSegment, setSelectedSegment] = useState(null)
    const [error, setError] = useState(null)
    const itemsPerPage = 10
    const username = user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : user.username || 'Unknown'

    // Refactored error handling
    const parseErrorMessage = (error) => {
        if (error.response) {
            const { status, data } = error.response

            if (status === 500) {
                return ERROR_MESSAGES.SERVER_ERROR
            }

            if (data?.message) {
                return `เกิดข้อผิดพลาด: ${data.message}`
            }
        } else if (error.request) {
            return ERROR_MESSAGES.NETWORK_ERROR
        }

        return ERROR_MESSAGES.DEFAULT
    }

    const getSegments = async () => {
        setIsLoading(true)
        setError(null)
        try {
            const params = {
                page: activePage,
                limit: itemsPerPage
            }

            if (searchQuery) {
                params.search = searchQuery
            }

            const { data } = await axios.get(`${process.env.REACT_APP_API_ROOT}/segment/`, {
                params,
                withCredentials: true
            })

            setSegments(data.segments || [])
            setTotalCount(data.total || 0)
            setTotalPages(data.totalPages || 1)
        } catch (error) {
            console.error('Error fetching segments:', error)
            const errorMessage = parseErrorMessage(error)
            setError(errorMessage)
            setSegments([])
            setTotalCount(0)
            setTotalPages(1)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!hasViewPermission) {
            props.history.push('/')
            return
        }
        getSegments()
    }, [activePage, searchQuery, hasViewPermission])

    const handlePageChange = (e, { activePage }) => {
        setActivePage(activePage)
    }

    const handleSearchChange = (e) => {
        setFilterText(e.target.value)
    }

    const handleSearch = () => {
        setSearchQuery(filterText)
        setActivePage(1)
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const handleExport = (segment) => {
        setSelectedSegment(segment)
        setOpenExportModal(true)
    }

    const handleCloseExportModal = () => {
        setOpenExportModal(false)
        setSelectedSegment(null)
    }

    const dateFormatter = (cell) => {
        return moment(cell).format('DD/MM/YYYY HH:mm')
    }

    const actionButtonFormatter = (cell, row) => {
        return (
            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                <SegmentForm
                    value={row}
                    createBy={username}
                    apiUrl={`${process.env.REACT_APP_API_ROOT}/segment/save`}
                    renderButton={(props) => (
                        <button {...props} className="btn btn-primary btn-sm" disabled={!hasEditPermission}>
                            <i className="fas fa-edit"></i> แก้ไข
                        </button>
                    )}
                    onSuccess={() => getSegments()}
                />
                <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleExport(row)}
                    disabled={!hasEditPermission}
                >
                    <i className="fas fa-download"></i> Export
                </button>
            </div>
        )
    }
    return (
        <>
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">
                    <Alert.Heading>เกิดข้อผิดพลาด</Alert.Heading>
                    <p className="mb-0">{error}</p>
                </Alert>
            )}

            <div className="card shadow mb-4">
                <div className="card-header py-3">
                    <h6 className="m-0 font-weight-bold text-primary">Audience Builder Dashboard</h6>
                </div>
                <div className="card-body">
                    {/* Filter Section */}
                    <div className="row mb-3">
                        <div className="col-12 mb-2">
                            <SegmentForm
                                apiUrl={`${process.env.REACT_APP_API_ROOT}/segment/save`}
                                createBy={username}
                                onSuccess={() => getSegments()}
                                renderButton={(props) => (
                                    <button {...props} className='btn btn-success btn-sm px-3' disabled={!hasEditPermission}>
                                        สร้าง +
                                    </button>
                                )}
                            />
                        </div>
                        <div className="col-6">
                            <div className="input-group md-form form-sm form-2 pl-0">
                                <input
                                    className="form-control my-0 py-1 lime-border"
                                    type="text"
                                    placeholder="ค้นหาชื่อ Segment..."
                                    aria-label="Search"
                                    value={filterText}
                                    onChange={handleSearchChange}
                                    onKeyPress={handleKeyPress}
                                />
                                <div className="input-group-append">
                                    <span
                                        className="input-group-text lime lighten-2"
                                        id="basic-text1"
                                        style={{ cursor: 'pointer' }}
                                        onClick={handleSearch}
                                    >
                                        <i className="fas fa-search text-grey" aria-hidden="true"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive" style={{ overflowX: 'auto' }}>
                        {isLoading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" role="status" variant="primary">
                                    <span className="sr-only">Loading...</span>
                                </Spinner>
                                <div className="mt-2">กำลังโหลดข้อมูล...</div>
                            </div>
                        ) : (
                            <BootstrapTable
                                data={segments}
                                className="table table-striped"
                                keyField="_id"
                                options={{ noDataText: 'ไม่พบข้อมูล' }}
                                tableStyle={{ minWidth: '800px' }}
                            >
                                <TableHeaderColumn dataField="title" width="55%">
                                    ชื่อ Segment
                                </TableHeaderColumn>
                                <TableHeaderColumn dataField="createBy" width="15%">
                                    สร้างโดย
                                </TableHeaderColumn>
                                <TableHeaderColumn
                                    dataField="updatedAt"
                                    width="15%"
                                    dataFormat={dateFormatter}
                                >
                                    แก้ไขล่าสุด
                                </TableHeaderColumn>
                                <TableHeaderColumn
                                    dataField="_id"
                                    width="15%"
                                    dataFormat={actionButtonFormatter}
                                    dataAlign="center"
                                >
                                    จัดการ
                                </TableHeaderColumn>
                            </BootstrapTable>
                        )}
                    </div>

                    {/* Pagination */}
                    <div className="row mt-3">
                        <div className="col-12">
                            {segments.length > 0 && !isLoading && (
                                <Pagination
                                    activePage={activePage}
                                    onPageChange={handlePageChange}
                                    totalPages={totalPages}
                                    ellipsisItem={null}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Export Modal */}
            <ExportSegmentModal
                open={openExportModal}
                close={handleCloseExportModal}
                segmentData={selectedSegment}
            />
        </>
    )
}

export default Dashboard